import { z } from 'zod';
// Saved views — one saveable/shareable store for every work-view family
// (tickets/board/backlog/roadmap). Coexists with SavedFilter (AQL-only filters).
export const SavedViewType = z.enum([
    'tickets', 'board', 'backlog', 'roadmap', 'calendar', 'timeline',
]);
export const ViewVisibility = z.enum(['private', 'team', 'public']);
/** View-specific state bag. `passthrough` so each view family can stash extras. */
export const SavedViewConfig = z
    .object({
    filters: z.record(z.unknown()).optional(),
    sort: z.string().optional(),
    columns: z.array(z.string()).optional(), // visible column ids, in display order
    columnWidths: z.record(z.number()).optional(),
    pinned: z.array(z.string()).optional(),
    groupBy: z.string().optional(),
    swimlane: z.string().optional(),
    pageSize: z.number().optional(),
    layout: z.record(z.unknown()).optional(),
})
    .passthrough();
export const SavedView = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    ownerId: z.string().nullable(),
    shared: z.boolean(),
    visibility: ViewVisibility,
    viewType: SavedViewType,
    ticketType: z.string().nullable(),
    queryText: z.string(),
    config: SavedViewConfig,
    isDefault: z.boolean(),
    defaultForRole: z.string().nullable(),
    defaultForTeam: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CreateSavedViewInput = z.object({
    name: z.string().min(1).max(120),
    description: z.string().optional(),
    viewType: SavedViewType.default('tickets'),
    ticketType: z.string().optional(),
    visibility: ViewVisibility.optional(),
    shared: z.boolean().optional(),
    queryText: z.string().optional(),
    config: SavedViewConfig.optional(),
    isDefault: z.boolean().optional(),
    defaultForRole: z.string().optional(),
    defaultForTeam: z.string().optional(),
});
export const UpdateSavedViewInput = CreateSavedViewInput.partial();
/** Master list of selectable ticket columns. `sortable` mirrors the backend's allowed sort fields. */
export const TICKET_COLUMNS = [
    { id: 'key', label: 'Key', sortable: true, pinnable: true, alwaysVisible: true },
    { id: 'type', label: 'Type' },
    { id: 'title', label: 'Title', pinnable: true },
    { id: 'status', label: 'Status', sortable: true },
    { id: 'priority', label: 'Priority', sortable: true },
    { id: 'assignee', label: 'Assignee' },
    { id: 'ballWith', label: 'Ball with' },
    { id: 'requester', label: 'Requested By' },
    { id: 'customer', label: 'Customer' },
    { id: 'tags', label: 'Tags' },
    { id: 'parent', label: 'Parent' },
    { id: 'sprint', label: 'Sprint' },
    { id: 'release', label: 'Release' },
    { id: 'component', label: 'Component' },
    { id: 'severity', label: 'Severity' },
    { id: 'environment', label: 'Environment' },
    { id: 'storyPoints', label: 'Points' },
    { id: 'estimateHours', label: 'Estimate (h)' },
    { id: 'remainingHours', label: 'Remaining (h)' },
    { id: 'impactArr', label: 'Impact ARR' },
    { id: 'impactLtv', label: 'LTV' },
    { id: 'impactTcv', label: 'TCV' },
    { id: 'source', label: 'Source' },
    { id: 'legacy', label: 'Legacy' },
    { id: 'dueDate', label: 'Due', sortable: true },
    { id: 'updated', label: 'Updated', sortable: true },
    { id: 'created', label: 'Created', sortable: true },
    { id: 'ageDays', label: 'Age (days)', sortable: true },
];
export const TICKET_COLUMN_IDS = TICKET_COLUMNS.map((c) => c.id);
export const TICKET_DEFAULT_COLUMNS = [
    'key', 'type', 'title', 'status', 'priority', 'assignee', 'ballWith', 'requester', 'customer', 'tags', 'updated',
];
/** Per-ticket-type default visible columns (Advanced Views spec §4.4). */
export const TICKET_COLUMNS_BY_TYPE = {
    feature_request: ['key', 'type', 'title', 'parent', 'status', 'priority', 'assignee', 'ballWith', 'requester', 'customer', 'storyPoints', 'sprint', 'release', 'tags', 'updated'],
    change_request: ['key', 'type', 'title', 'parent', 'status', 'priority', 'assignee', 'ballWith', 'requester', 'customer', 'storyPoints', 'sprint', 'release', 'tags', 'updated'],
    internal_bug: ['key', 'type', 'title', 'severity', 'priority', 'customer', 'component', 'environment', 'status', 'assignee', 'ballWith', 'requester', 'release', 'tags', 'updated'],
    customer_support: ['key', 'type', 'title', 'customer', 'priority', 'severity', 'status', 'assignee', 'ballWith', 'requester', 'tags', 'updated'],
    epic: ['key', 'type', 'title', 'status', 'ballWith', 'storyPoints', 'release', 'dueDate', 'requester', 'customer', 'tags', 'updated'],
    task: TICKET_DEFAULT_COLUMNS,
};
/** Resolve the default visible columns for a (optional) ticket type. */
export function defaultColumnsForType(type) {
    return [...((type && TICKET_COLUMNS_BY_TYPE[type]) || TICKET_DEFAULT_COLUMNS), 'ageDays'];
}
//# sourceMappingURL=view.js.map